/**
 * Date/time formatting helpers tuned for chat UX.
 *
 * All "today/yesterday/older" decisions are local-time. Russian locale.
 */

import i18n from '@/i18n';

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isYesterday(d: Date, now = new Date()): boolean {
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  return isSameDay(d, y);
}

const dateFmt = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const timeFmt = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
});

/** Header for a day-separator: "Сегодня" / "Вчера" / "9 мая 2026 г." */
export function formatDaySeparator(d: Date, now = new Date()): string {
  if (isSameDay(d, now)) return i18n.t('lib2.day.today');
  if (isYesterday(d, now)) return i18n.t('lib2.day.yesterday');
  return dateFmt.format(d);
}

/** Short HH:MM time used next to author name and on hover. */
export function formatTime(d: Date): string {
  return timeFmt.format(d);
}

/**
 * Inline metadata after the author name: "Сегодня в 14:32" or
 * "9 мая 2026 г. в 14:32" — used on the leading message in a group.
 */
export function formatMessageMeta(d: Date, now = new Date()): string {
  if (isSameDay(d, now)) return i18n.t('lib2.day.todayAt', { time: formatTime(d) });
  if (isYesterday(d, now)) return i18n.t('lib2.day.yesterdayAt', { time: formatTime(d) });
  return i18n.t('lib2.day.dateAt', { date: dateFmt.format(d), time: formatTime(d) });
}
